import React, { createContext, useContext, useState, ReactNode, useCallback, Fragment, useRef, ReactElement } from 'react';
import { CToast, CToastHeader, CToastBody, CButton, CAlert, CToaster, CToastClose } from '@coreui/react';
import CIcon from '@coreui/icons-react';

type ToastActionType = 'primary' | 'info' | 'warning' | 'error' | 'danger' | 'secondary';

type ToastActionVisual = {
  text: string;
  type?: ToastActionType;
  onClick?: () => void;
};

export type ToastAction = ToastActionVisual & {};

type ToastMessageSegment =
  | string
  | {
      message: string;
      icon?: string[];
      className?: string;
      alertColor?: string;
    };

export interface ToastOptions {
  title: string;
  message: ToastMessageSegment | ToastMessageSegment[];
  headerClassName?: string;
  autohide?: boolean;
}

type ShowToastInput = ToastOptions & {
  actions: readonly ToastAction[];
};
type ShowToastErrorInput = Partial<ToastOptions> &
  Pick<ToastOptions, 'message'> & {
    additionalActions?: readonly ToastActionVisual[];
  };
type ShowToastSuccessInput = ShowToastErrorInput;

interface ToastContextType {
  showToast: (i: ShowToastInput) => void;

  showToastSuccess: (i: ShowToastErrorInput) => void;
  showToastError: (i: ShowToastErrorInput) => void;
}

type InternalToast = ShowToastInput & {
  id: string;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toast, addToast] = useState<React.ReactElement | undefined>(undefined);

  const toaster = useRef<HTMLDivElement | null>(null);

  const showToast = useCallback((options: ShowToastInput): void => {
    addToast(
      renderToast({
        id: 'T' + new Date().getTime() + '-' + Math.random().toString(36),
        ...options,
        autohide: options.autohide ?? true,
      }),
    );
  }, []);

  const showToastError = useCallback(
    (options: ShowToastErrorInput): void => {
      return showToast({
        headerClassName: 'bg-danger text-white',
        ...options,
        title: options?.title || 'Attenzione',
        message: options?.message,
        actions: [...(options?.additionalActions ?? []).map((a) => ({ ...a, result: 'action' }))] as const,
      });
      return;
    },
    [showToast],
  );

  const showToastSuccess = useCallback(
    (options: ShowToastSuccessInput): void => {
      return showToast({
        headerClassName: 'bg-success text-white',
        ...options,
        title: options?.title || 'Operazione completata',
        message: options?.message,
        actions: [...(options?.additionalActions ?? []).map((a) => ({ ...a, result: 'action' }))] as const,
      });
      return;
    },
    [showToast],
  );

  const ctxValue: ToastContextType = {
    showToast,
    showToastSuccess,
    showToastError,
  };

  const renderMessageSegment = (segment: ToastMessageSegment | ToastMessageSegment[]): ReactNode => {
    if (Array.isArray(segment)) {
      return segment.map((s, i) => {
        return <Fragment key={'message-segment-' + i}>{renderMessageSegment(s)}</Fragment>;
      });
    }

    if (typeof segment === 'string') {
      return <p>{segment}</p>;
    }
    let out = <>{segment.message}</>;
    if (segment.icon) {
      out = (
        <>
          <CIcon icon={segment.icon} className={'me-2'}></CIcon> {out}
        </>
      );
    }
    if (segment.className) {
      out = <span className={segment.className}>{out}</span>;
    }
    if (segment.alertColor) {
      out = <CAlert color={segment.alertColor}>{out}</CAlert>;
    }
    return out;
  };

  const renderToast = (t: InternalToast) => {
    return (
      <CToast autohide={t.autohide} visible={true} key={'toast-' + t.id}>
        {!!t.title && (
          <CToastHeader className={(t.headerClassName || '') + ' d-flex align-items-center justify-content-between'}>
            <div>{t.title}</div>
            <CToastClose />
          </CToastHeader>
        )}
        <CToastBody>
          <div className="d-flex justify-content-between">
            <div>
              {!!t.message && renderMessageSegment(t.message)}

              {!!t.actions.length && (
                <div className="mt-2 pt-2 border-top">
                  {t.actions.map((action, idx) => (
                    <CButton
                      size={'sm'}
                      key={'toast-action-' + idx}
                      color={mapTypeToColor(action.type)}
                      onClick={() => {
                        if (action.onClick) {
                          action.onClick();
                        }
                      }}
                      className={mapTypeToButtonClasses(action.type)}
                    >
                      {action.text}
                    </CButton>
                  ))}
                </div>
              )}
            </div>
            <div>{!t.title && <CToastClose />}</div>
          </div>
        </CToastBody>
      </CToast>
    );
  };

  return (
    <ToastContext.Provider value={ctxValue}>
      {children}
      <>
        <CToaster ref={toaster as any} push={toast as any} placement="top-end" />
      </>
    </ToastContext.Provider>
  );
};

// This helper function remains the same
const mapTypeToColor = (type?: ToastActionType): string => {
  switch (type) {
    case 'danger':
    case 'error':
      return 'danger';
    case 'warning':
      return 'warning';
    case 'secondary':
      return 'secondary';
    case 'info':
    case 'primary':
    default:
      return 'primary';
  }
};

const mapTypeToButtonClasses = (type?: ToastActionType): string => {
  switch (type) {
    case 'danger':
    case 'error':
      return 'text-white';
    case 'warning':
      return '';
    case 'info':
    case 'primary':
    default:
      return '';
  }
};

// This custom hook remains the same
export const useToast = (): ToastContextType => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
};
